import React, { useState, useEffect } from 'react';
import { SystemUser, Profile, UserFormData } from '../../types';
import { userService } from '../../services/userService';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';

interface UserFormModalProps {
  user: SystemUser | null;
  profiles: Profile[];
  onClose: () => void;
  onSuccess: () => void;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  user,
  profiles,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    profile_id: profiles[0]?.id || 1,
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || user.nome || '',
        email: user.email,
        password: '',
        password_confirmation: '',
        profile_id: user.profile_id,
        active: user.active,
      });
    }
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validations
    if (!formData.name.trim()) {
      setError('O nome é obrigatório');
      return;
    }

    if (!formData.email.trim()) {
      setError('O email é obrigatório');
      return;
    }

    if (!user && !formData.password) {
      setError('A senha é obrigatória para novos usuários');
      return;
    }

    if (formData.password && formData.password !== formData.password_confirmation) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    try {
      setLoading(true);

      const dataToSubmit: UserFormData = {
        ...formData,
      };

      // Remove password fields if editing and password is empty
      if (user && !formData.password) {
        delete dataToSubmit.password;
        delete dataToSubmit.password_confirmation;
      }

      if (user) {
        await userService.update(user.id, dataToSubmit);
      } else {
        await userService.create(dataToSubmit);
      }

      onSuccess();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          'Erro ao salvar usuário. Verifique os dados e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={user ? 'Editar Usuário' : 'Novo Usuário'}
    >
      <form onSubmit={handleSubmit} className="p-6">
        {error && (
          <Alert type="error" className="mb-4">
            {error}
          </Alert>
        )}

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Completo *
            </label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Digite o nome completo"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@exemplo.com"
              required
            />
          </div>

          {/* Profile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Perfil de Acesso *
            </label>
            <select
              name="profile_id"
              value={formData.profile_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name || profile.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Senha {!user && '*'}
            </label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={user ? 'Deixe em branco para manter a atual' : 'Mínimo 6 caracteres'}
              required={!user}
            />
            {user && (
              <p className="text-xs text-gray-500 mt-1">
                Deixe em branco para manter a senha atual
              </p>
            )}
          </div>

          {/* Password Confirmation */}
          {formData.password && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar Senha *
              </label>
              <Input
                type="password"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleChange}
                placeholder="Digite a senha novamente"
                required
              />
            </div>
          )}

          {/* Active */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
              Usuário ativo
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Salvando...' : user ? 'Atualizar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserFormModal;
